<script>
  import { _ } from "svelte-i18n";
  import { router } from "tinro";
  import { BASE_URL } from "../utilities/config.js";
  import { createEventDispatcher, onMount, onDestroy, tick } from "svelte";
  import {
    currentPage,
    exerciseId,
    userId,
    userName,
    assessmentType,
    assessmentId,
    assessmentState,
    assessmentDefinitionId,
    finalScoreDetail,
    invitationNumber,
  } from "../utilities/DataStore";
  import {
    submitFinalAssessmentResults,
    callEvaluationService,
  } from "./components/UploadResults.svelte";
  import { api } from "../utilities/Utils.svelte";

  onMount(async () => {
    initialize();
  });

  onDestroy(() => {
    _destroyed = true;
    if (intervalId) {
      clearInterval(intervalId);
    }
  });

  export let instanceId;
  let exerciseListStatus = "NEW";
  let exerciseListData;
  let progressListData;
  let instanceData;

  let combinedData = undefined;
  let errorMsg;

  let progressStepsEl;
  let currentSectionIndex = -1;

  let progressDoneCount = 0;
  let progressFirstActive = undefined;

  // Display index is offset by 1 because the first hidden intro section counts as Section 1.
  $: displaySectionIndex = combinedData?.length > 0
    ? Math.min(combinedData.length - 1, Math.max(0, currentSectionIndex + 1))
    : currentSectionIndex;

  let progressTime = 0;
  let progressRemaining;

  let submitFinalAssessmentResultsStatus;

  let intervalId;
  let _destroyed = false;
  let _finalizingSection = false;
  let sectionComplete = false;

  function useFallbackProgressSteps() {
    combinedData = [
      {
        id: $assessmentDefinitionId || "CURRENT_SECTION",
        name: $assessmentType || "Current Section",
        status: "PENDING",
      },
    ];
    currentSectionIndex = 0;
    progressDoneCount = 0;
  }

  function initialize() {
    console.log("[SCORING-DIAG] AssessmentOverview initialize() called", {
      instanceId,
      assessmentDefinitionId: $assessmentDefinitionId,
      timestamp: new Date().toISOString(),
    });

    Promise.all([loadData2(), loadSectionProgress()]).then(() => {
      if (_destroyed) return;

      console.log("[SCORING-DIAG] initialize - loadData2 completed", {
        instanceId,
        nextExerciseId: instanceStatusData?.nextExerciseId,
        loadingState,
      });

      console.log("combinedData", combinedData);

      console.log("[SCORING-DIAG] initialize - scheduling updateProgress every 5000ms (setInterval)");
      intervalId = window.setInterval(() => {
        updateProgress();
      }, 5000);

      centerCurrentStep();
    });
  }

  function normalizeSectionStatus(section) {
    if (section?.invDefStatus === "CANCEL" || section?.invDefStatus === "EXPIRED") {
      return "CANCELLED";
    }
    if (section?.status === "SCORED") {
      return "DONE";
    }
    return "PENDING";
  }

  async function loadSectionProgress() {
    if (!$invitationNumber) {
      useFallbackProgressSteps();
      await tick();
      centerCurrentStep();
      return;
    }

    try {
      const sections = await api
        .get(`public/assessment/invitation/${$invitationNumber}/definitions-detail`)
        .json();

      combinedData = (sections || []).map((s) => ({
        id: s.idDefinition,
        name: s.name,
        status: normalizeSectionStatus(s),
      }));

      currentSectionIndex = combinedData.findIndex(
        (s) => s.id === $assessmentDefinitionId,
      );

      if (currentSectionIndex < 0) {
        currentSectionIndex = combinedData.findIndex((s) => s.status === "PENDING");
      }

      if (currentSectionIndex < 0 && combinedData.length > 0) {
        currentSectionIndex = combinedData.length - 1;
      }

      progressDoneCount = combinedData.filter((s) => s.status === "DONE").length;
      await tick();
      centerCurrentStep();
    } catch (error) {
      console.error("loadSectionProgress error", error);
      useFallbackProgressSteps();
      await tick();
      centerCurrentStep();
    }
  }

  function centerCurrentStep() {
    if (!progressStepsEl || currentSectionIndex < 0) {
      return;
    }
    const currentStep = progressStepsEl.querySelector(".progress-step.current");
    if (currentStep) {
      currentStep.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }

  let instanceStatusData;
  let loadingState = "NEW";

  function loadData2() {
    loadingState = "LOADING";
    console.log("[SCORING-DIAG] loadData2 called", {
      instanceId,
      previousLoadingState: loadingState,
      timestamp: new Date().toISOString(),
    });

    return api
      .get(`public/assessment/instance/${instanceId}/status`)
      .json()
      .then((data) => {
        instanceStatusData = data;
        console.log("[SCORING-DIAG] loadData2 status response", {
          instanceId,
          nextExerciseId: data?.nextExerciseId,
          hasNextExercise: data?.nextExerciseId != null,
          fullStatusData: data,
          timestamp: new Date().toISOString(),
        });
        loadingState = "OK";

        if (data?.nextExerciseId) {
          console.log("[SCORING-DIAG] loadData2 - more exercises remain, opening next", { nextExerciseId: data.nextExerciseId });
          openExercise(data?.nextExerciseId);
        } else {
          console.log("[SCORING-DIAG] loadData2 - nextExerciseId is null/undefined, all exercises may be done");
          // Single finalize guard, shared with submitFinalResult(): never run two
          // finalize attempts at once.
          if (_finalizingSection || submitFinalAssessmentResultsStatus === "LOADING") {
            return;
          }

          _finalizingSection = true;
          submitFinalAssessmentResultsStatus = "LOADING";
          // Stop the 5s poll the moment finalization starts so it cannot fire a
          // second finalize while this one is in flight.
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }

          submitFinalAssessmentResults(instanceId)
            .then(() => {
              // /end succeeded — the instance is SUBMITTED and will be scored.
              submitFinalAssessmentResultsStatus = "OK";
              $currentPage = "AssessmentSelection";
            })
            .catch((error) => {
              // /end failed: do NOT advance as if the section were done. Surface a
              // retry instead of silently dropping the submission.
              console.error("[SCORING-DIAG] submitFinalAssessmentResults (section finalize) FAILED - showing retry", {
                instanceId,
                error: error?.message || error,
                timestamp: new Date().toISOString(),
              });
              if (typeof window !== "undefined" && window.TrackJS) {
                try {
                  window.TrackJS.track(
                    error instanceof Error
                      ? error
                      : new Error("section finalize failed: " + error),
                  );
                } catch (_) {
                  /* never let logging break the flow */
                }
              }
              _finalizingSection = false;
              submitFinalAssessmentResultsStatus = "ERROR";
            });
        }
      })
      .catch((error) => {
        console.error("[SCORING-DIAG] loadData2 FAILED", {
          instanceId,
          error: error?.message || error,
          errorName: error?.name,
          timestamp: new Date().toISOString(),
        });
        errorMsg = "ERROR_LOADING_ASSESSMENT_STATUS";
        loadingState = "ERROR";
      });
  }

  function initialize2() {
    Promise.allSettled([
      loadData(),
      loadDataProgress(),
      loadInstanceData(),
    ]).then(() => {
      console.log("allLoaded");

      combinedData = exerciseListData.map((i) => {
        let p = progressListData.find((x) => x?.id === i?.id);

        if (p) {
          i["status"] = "DONE";
          i["overallScore"] = p.overallScore;
          progressDoneCount++;
        } else {
          if (!progressFirstActive) {
            progressFirstActive = i.id;
          }
        }

        return i;
      });
      console.log("combinedData", combinedData);

      if (progressDoneCount < combinedData.length) {
        openExercise(progressFirstActive);
      }

      intervalId = window.setInterval(() => {
        updateProgress();
      }, 1000);
    });
  }

  async function loadData() {
    exerciseListStatus = "LOADING";
    return fetch(
      BASE_URL +
        `/public/assessment/definition/` +
        $assessmentDefinitionId +
        `/exercise`,
    )
      .then((response) => {
        if (!response.ok) {
          console.error("response error");

          throw new Error("Network response was not ok");
        }
        console.log("response ok", response);
        return response.json();
      })
      .then((data) => {
        exerciseListData = data;
        console.log("exerciseListData", exerciseListData);
        exerciseListStatus = "OK";
      })
      .catch((error) => {
        console.error("catch error", error);
        errorMsg = "ERROR_LOADING_ASSESSMENT";
        exerciseListData = undefined;
        exerciseListStatus = "ERROR";
      });
  }

  async function loadInstanceData() {
    exerciseListStatus = "LOADING";
    return fetch(BASE_URL + `/public/assessment/instance/` + instanceId)
      .then((response) => {
        if (!response.ok) {
          console.error("response error");

          throw new Error("Network response was not ok");
        }
        console.log("response ok", response);
        return response.json();
      })
      .then((data) => {
        instanceData = data;
        console.log(
          Date.parse(instanceData?.createdAt) /
            Date.parse(instanceData?.deadlineAt),
        );
        console.log("instanceData", instanceData);
        exerciseListStatus = "OK";
        updateProgress();
      })
      .catch((error) => {
        console.error("catch error", error);
        errorMsg = "ERROR_LOADING_ASESSMENT_INSTANCE";
        instanceData = undefined;
        exerciseListStatus = "ERROR";
      });
  }

  async function loadDataProgress() {
    exerciseListStatus = "LOADING";
    return fetch(
      BASE_URL + `/public/assessment/instance/` + instanceId + `/progress`,
    )
      .then((response) => {
        if (!response.ok) {
          console.error("response error");

          throw new Error("Network response was not ok");
        }
        console.log("response ok", response);
        return response.json();
      })
      .then((data) => {
        progressListData = data;
        console.log("progressListData", progressListData);
        exerciseListStatus = "OK";
      })
      .catch((error) => {
        console.log("catch error", error);
        errorMsg = "ERROR_LOADING_ASSESSMENT";
        progressListData = undefined;
        exerciseListStatus = "ERROR";
      });
  }

  function openExercise(exercise) {
    console.log("openExercise", exercise);
    $currentPage = "AssessmentExercise";
    $exerciseId = exercise;
    //router.goto("/assessment/" + instanceId + "/exercise/" + exerciseId);
  }

  function submitFinalResult() {
    console.log("[SCORING-DIAG] submitFinalResult called", {
      instanceId,
      assessmentDefinitionId: $assessmentDefinitionId,
      submitFinalAssessmentResultsStatus,
      timestamp: new Date().toISOString(),
    });

    // Single finalize guard: never run two finalize attempts at once (the section
    // finalize in loadData2 sets the same status / _finalizingSection flag).
    if (submitFinalAssessmentResultsStatus === "LOADING" || _finalizingSection) {
      console.warn("submitFinalResult: already finalizing, ignoring duplicate call");
      return;
    }
    submitFinalAssessmentResultsStatus = "LOADING";

    // Stop the 5s poll the moment finalization starts.
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    submitFinalAssessmentResults(instanceId)
      .then(async () => {
        // /end succeeded — the instance is SUBMITTED and will be scored.
        console.log("[SCORING-DIAG] submitFinalAssessmentResults resolved (success)", {
          instanceId,
          timestamp: new Date().toISOString(),
        });

        await new Promise((resolve) => setTimeout(resolve, 1000));

        let scoreData;
        try {
          scoreData = await callEvaluationService($assessmentDefinitionId, instanceId);
        } catch (evalError) {
          // Triggering scoring is best-effort: the backend scheduler re-scores
          // SUBMITTED instances every ~5 min, so a failure here only delays the
          // result. Proceed to the results screen (which handles a pending score)
          // rather than blocking the student.
          console.error("[SCORING-DIAG] callEvaluationService failed (non-fatal, scheduler will re-score)", {
            instanceId,
            error: evalError?.message || evalError,
            timestamp: new Date().toISOString(),
          });
        }

        submitFinalAssessmentResultsStatus = "OK";
        $assessmentState = "DONE";
        $currentPage = "AssessmentResults";
        $finalScoreDetail = scoreData;
        console.log("[SCORING-DIAG] navigating to certificate", {
          instanceId,
          finalScoreDetail: scoreData,
          assessmentState: "DONE",
        });
        router.goto("/certificate/" + instanceId);
      })
      .catch((error) => {
        // /end failed: the instance is NOT submitted. Do NOT navigate as if done —
        // surface a retry so the student can resubmit instead of silently losing
        // their results.
        console.error("[SCORING-DIAG] submitFinalAssessmentResults FAILED - showing retry", {
          instanceId,
          error: error?.message || error,
          timestamp: new Date().toISOString(),
        });
        submitFinalAssessmentResultsStatus = "ERROR";
      });
  }

  // Retry a failed finalization. Re-runs loadData2(), which re-evaluates whether
  // exercises remain and re-attempts the (idempotent) /end submit.
  function retryFinalSubmit() {
    submitFinalAssessmentResultsStatus = undefined;
    _finalizingSection = false;
    loadData2();
  }

  function updateProgress() {
    //   console.log(Date.parse(instanceData?.createdAt + "Z"));
    //   console.log(Date.parse(instanceData?.deadlineAt + "Z"));

    //   console.log( Date.now() -
    //     Date.parse(instanceData?.createdAt + "Z"));

    //   console.log((new Date(
    //     Date.now() + new Date().getTimezoneOffset() * 60000
    //   ).getTime()));

    //   console.log((Date.parse(instanceData?.deadlineAt + "Z") -
    //       Date.parse(instanceData?.createdAt + "Z")));

    //   console.log(Date.parse(instanceData?.createdAt + "Z"));
    progressTime = (
      ((Date.now() - Date.parse(instanceData?.createdAt + "Z")) /
        (Date.parse(instanceData?.deadlineAt + "Z") -
          Date.parse(instanceData?.createdAt + "Z"))) *
      100
    ).toFixed(0);

    let remainingMs = Date.parse(instanceData?.deadlineAt + "Z") - Date.now();
    let remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    progressRemaining =
      Math.floor(remainingSeconds / 60) +
      ":" +
      ((remainingSeconds % 60) + "").padStart(2, "0");
    if (remainingMs <= 0) {
      $assessmentState = "DEADLINE";
    }

    //FIXME
    console.log("[SCORING-DIAG] updateProgress decision", {
      instanceId,
      nextExerciseId: instanceStatusData?.nextExerciseId,
      nextExerciseIdIsNull: instanceStatusData?.nextExerciseId == null,
      submitFinalAssessmentResultsStatus,
      willSubmitFinal: instanceStatusData?.nextExerciseId == null && submitFinalAssessmentResultsStatus !== "LOADING",
      willLoadData2: !(instanceStatusData?.nextExerciseId == null && submitFinalAssessmentResultsStatus !== "LOADING"),
      timestamp: new Date().toISOString(),
    });
    if (instanceStatusData?.nextExerciseId != null) {
      // More exercises remain — refresh status.
      console.log("[SCORING-DIAG] updateProgress -> calling loadData2()");
      loadData2();
    } else if (
      submitFinalAssessmentResultsStatus !== "LOADING" &&
      submitFinalAssessmentResultsStatus !== "OK" &&
      submitFinalAssessmentResultsStatus !== "ERROR" &&
      !_finalizingSection
    ) {
      // No more exercises and not already finalizing/done — finalize once.
      // Skipped while ERROR so the poll does not loop-retry; the student retries
      // explicitly via the RETRY button.
      console.log("[SCORING-DIAG] updateProgress -> calling submitFinalResult()");
      submitFinalResult();
    }
    // else: finalize is in progress / done / awaiting retry — do nothing.

    // if (
    //   progressDoneCount === combinedData?.length &&
    //   submitFinalAssessmentResultsStatus !== "LOADING"
    // ) {
    //   submitFinalResult();
    // }
  }
</script>

<div class="container">
  {#if _finalizingSection}
    <div class="overview-skeleton" aria-label={$_("LOADING")}>
      <div class="skeleton-line skeleton-title"></div>
      <div class="skeleton-line skeleton-subtitle"></div>
      <div class="skeleton-block skeleton-question"></div>
      <div class="skeleton-block skeleton-option"></div>
      <div class="skeleton-block skeleton-option"></div>
      <div class="skeleton-block skeleton-option"></div>
      <div class="skeleton-block skeleton-submit"></div>
    </div>
  {:else}
    <div class="row align-items-center" style="display: none;">
      <div class="col">
        <div class="lesson-title">
          {$assessmentType || ""}
        </div>
      </div>
      <div class="col-auto lesson-detail-box">
        <div class="lesson-detail float-right">
          <div>Assessment: {$assessmentId}</div>
          <div style="display: none;">Instance: {instanceId}</div>
          <div>UserName: {$userName}</div>
          <div style="display: none;">UserId: {$userId}</div>
          <div style="display: none;">State: {$assessmentState}</div>
        </div>
      </div>
    </div>

    <!-- Progress Indicator -->
    {#if loadingState === "OK" && combinedData?.length > 1}
    <div class="progress-section px-4 py-4">
      <div class="progress-steps" bind:this={progressStepsEl}>
        {#each combinedData || [] as exercise, i}
          <div
            class="progress-step"
            class:active={i <= displaySectionIndex || exercise.status === "DONE"}
            class:current={i === displaySectionIndex}
          >
            <div class="progress-number">{#if i < displaySectionIndex || exercise.status === "DONE"}✓{:else}{i + 1}{/if}</div>
            <div class="progress-label sr-only">{exercise.name || exercise.exerciseType}</div>
          </div>
        {/each}
      </div>
      <div class="progress-bar-container">
        <div
          class="progress-bar"
          style="width: {combinedData?.length > 1 && displaySectionIndex >= 0 ? (displaySectionIndex / (combinedData.length - 1)) * 100 : 0}%;"
        ></div>
      </div>
    </div>
    {/if}

    <div class="card-group">
      {#if submitFinalAssessmentResultsStatus === "ERROR"}
        <div class="overview-skeleton" style="align-items:center;gap:1rem;">
          <p class="submit-error">{$_("UPLOAD_FAILED_RETRY")}</p>
          <button class="btn btn-primary btn-cta" on:click={retryFinalSubmit}
            >{$_("RETRY")}</button
          >
        </div>
      {:else if loadingState === "NEW" || loadingState === "LOADING"}
        <div class="overview-loading-pass-through" aria-hidden="true"></div>
      {:else if loadingState === "ERROR"}
        Error loading assessment status
      {:else}
        <div class="overview-loading-pass-through" aria-hidden="true"></div>
      {/if}
    </div>

    <div class="card-group" style="display: none;">
      {#if exerciseListStatus === "NEW" || exerciseListStatus === "LOADING" || !combinedData}
        <div class="inline-skeleton" aria-label={$_("LOADING")}></div>
      {:else if exerciseListStatus === "ERROR"}
        Error loading lesson
      {:else}
        <div class="w-100 mb-1">
          <div style="min-width: 10em;">{$_("PROGRESS")}</div>
          <div class="progress">
            <div
              class="progress-bar"
              role="progressbar"
              style="width: {(progressDoneCount / combinedData?.length) * 100}%;"
              aria-valuenow={progressDoneCount}
              aria-valuemin="0"
              aria-valuemax={combinedData?.length}
            />
          </div>
          <div class="d-flex justify-content-center">
            <div>{progressDoneCount}/{combinedData?.length}</div>
          </div>
        </div>
        <div class="w-100 mb-1">
          <div style="min-width: 10em;">{$_("TIME")}</div>
          <div class="progress w-100 mb-1">
            <div
              class="progress-bar"
              class:bg-danger={progressTime >= 100}
              role="progressbar"
              style="width: {progressTime}%;"
              aria-valuenow={progressTime}
              aria-valuemin="0"
              aria-valuemax={100}
            />
          </div>
          <div class="d-flex justify-content-center">
            <div>
              {#if progressTime >= 100}
                100 % ({$_("REMAINING")}: --:--)
              {:else}
                {Math.min(100, progressTime)} % ({$_("REMAINING")}: {progressRemaining})
              {/if}
            </div>
          </div>
        </div>

        {#if progressDoneCount === combinedData?.length || $assessmentState === "DEADLINE"}
          {#if submitFinalAssessmentResultsStatus === "LOADING"}
            <span>{$_("PROCESSING_WAIT")}</span>
            <div class="inline-skeleton" aria-label={$_("LOADING")}></div>
          {:else}
            <div>
              <button class="btn btn-success" on:click={submitFinalResult}
                >{$_("SUBMIT_RESULT")}</button
              >
            </div>
          {/if}
        {/if}

        <ul class="list-group w-100" style="display: none;">
          {#each combinedData as l, i}
            <li
              class="list-group-item"
              class:active={l.id === progressFirstActive &&
                $assessmentState !== "DEADLINE"}
              class:disabled={l.status === "DONE" ||
                $assessmentState === "DEADLINE"}
              on:click={openExercise(l.id)}
            >
              <div>#{i + 1} - {l.name} - ({l.exerciseType})</div>
              {#if l.status}
                <div style="display:none;">
                  {l.status} - {l.overallScore}
                </div>{/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>

<style>
  @media (max-width: 768px) {
    .lesson-detail-box {
      display: none;
    }
  }

  .card-group {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .lesson-title {
    font-weight: 600;
    font-size: large;
  }

  .lesson-detail {
    border: 1px;
    border-color: #9d9fa2;
    border-style: solid;
    border-radius: 5px;
    margin: 0.5em;
    padding: 0.5em;
  }

  .progress-section {
    background: transparent;
    padding: 0.75rem 2rem 0.5rem;
  }

  .progress-steps {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 auto 0.75rem;
    max-width: 960px;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: thin;
  }

  .progress-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 1;
    min-width: 0;
  }

  .progress-step:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 23px;
    left: 50%;
    right: -50%;
    height: 3px;
    background-color: #d9e5ec;
    z-index: 1;
  }

  .progress-step.active:not(:last-child)::after {
    background-color: #36b9cc;
  }

  .progress-number {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: #ffffff;
    border: 2px solid #d3dce4;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.1rem;
    color: #788da1;
    margin-bottom: 0.4rem;
    z-index: 2;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  .progress-step.active .progress-number {
    background-color: #00b8d4;
    border-color: #00b8d4;
    color: #fff;
  }

  .progress-step.current .progress-number {
    box-shadow: 0 0 0 6px rgba(0, 184, 212, 0.2);
  }

  .progress-label {
    font-size: 0.75rem;
    text-align: center;
    color: #607d8b;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1.2;
    max-width: 90px;
    white-space: normal;
  }

  .progress-bar-container {
    width: 100%;
    height: 6px;
    background-color: #d9e5ec;
    border-radius: 3px;
    overflow: hidden;
    max-width: 960px;
    margin: 0 auto;
  }

  .progress-bar {
    height: 100%;
    background-color: #00b8d4;
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  .section-complete-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
    padding: 2rem;
  }

  .section-complete-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #00a094;
    color: #fff;
    font-size: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 16px rgba(0, 160, 148, 0.3);
  }

  .section-complete-title {
    font-size: 2rem;
    font-weight: 800;
    color: #10243c;
    margin-bottom: 0.75rem;
  }

  .section-complete-msg {
    font-size: 1.05rem;
    color: #475569;
    max-width: 480px;
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .btn-back-to-selection {
    background-color: #00a094;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.85rem 2.2rem;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .btn-back-to-selection:hover {
    background-color: #008880;
  }

  .overview-skeleton {
    width: min(560px, 90vw);
    margin: 0 auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .inline-skeleton,
  .skeleton-line,
  .skeleton-block {
    position: relative;
    overflow: hidden;
    background: #dce3ea;
    border-radius: 10px;
  }

  .inline-skeleton::after,
  .skeleton-line::after,
  .skeleton-block::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.65), transparent);
    animation: skeleton-shimmer 1.2s infinite;
  }

  .skeleton-title {
    height: 18px;
    width: 72%;
    margin: 0 auto;
  }

  .skeleton-subtitle {
    height: 14px;
    width: 46%;
    margin: 0 auto;
  }

  .inline-skeleton {
    height: 40px;
    width: 120px;
    margin: 0.5rem auto;
  }

  .skeleton-question {
    height: 42px;
    width: 92%;
    margin: 0.25rem auto 0.35rem;
  }

  .skeleton-option {
    height: 48px;
    width: 92%;
    margin: 0.35rem auto;
    border-radius: 8px;
  }

  .skeleton-submit {
    height: 56px;
    width: 220px;
    margin: 0.7rem auto 0;
    border-radius: 12px;
  }

  .loading-caption {
    margin-top: 0.4rem;
    color: #3b4d5d;
  }


  .overview-loading-pass-through {
    min-height: 1px;
  }
  .submit-error {
    color: #c0392b;
    font-size: 0.95rem;
    text-align: center;
    margin: 0;
  }

  @keyframes skeleton-shimmer {
    100% {
      transform: translateX(100%);
    }
  }

</style>
